import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  NodeTypes,
  useNodesState,
  useEdgesState,
  useReactFlow,
  useNodesInitialized,
} from '@xyflow/react';
import { SpinningLoader, Button, Chip, ContentSwitcher } from '@frontend/design-system';
import '@xyflow/react/dist/style.css';
import './dag-view.css';
import { useListRegistrationsQuery } from '../../queries/sales-demo.queries';
import { buildDagNodes } from '../../utils/dag-builder';
import { buildFlowGraph, applyDagreLayout, DagNodeData } from '../../utils/dag-layout';
import { DagNodeComponent } from './dag-node';
import { NodeDetailPanel } from './node-detail-panel';

const nodeTypes: NodeTypes = { dagNode: DagNodeComponent as any };

const DagFlowInner: React.FC<{
  registrationsData: any;
  isLoading: boolean;
  refetch: () => void;
}> = ({ registrationsData, isLoading, refetch }) => {
  const [ephemeralFilter, setEphemeralFilter] = useState('permanent');
  const [selectedNode, setSelectedNode] = useState<DagNodeData | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const { fitView } = useReactFlow();
  const nodesInitialized = useNodesInitialized();

  const filteredRegistrations = useMemo(() => {
    const regs = registrationsData?.registrations ?? [];
    if (ephemeralFilter === 'ephemeral') return regs.filter((r: any) => r.ephemeralId || r.ephemeral_id);
    if (ephemeralFilter === 'permanent') return regs.filter((r: any) => !r.ephemeralId && !r.ephemeral_id);
    return regs;
  }, [registrationsData?.registrations, ephemeralFilter]);

  const dagData = useMemo(() => {
    const dagNodes = buildDagNodes(filteredRegistrations);
    const { nodes, edges } = buildFlowGraph(dagNodes);
    return { nodes: applyDagreLayout(nodes, edges), edges };
  }, [filteredRegistrations]);

  const [nodes, setNodes, onNodesChange] = useNodesState(dagData.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(dagData.edges);

  useEffect(() => {
    setNodes(dagData.nodes);
    setEdges(dagData.edges);
  }, [dagData.nodes, dagData.edges, setNodes, setEdges]);

  // Fit the view only once ReactFlow has measured the custom node dimensions.
  // Fitting before measurement (e.g. via setTimeout) computes wrong bounds and
  // leaves the graph overflowing the container.
  useEffect(() => {
    if (nodesInitialized) {
      fitView({ padding: 0.15, maxZoom: 1.25, duration: 200 });
    }
  }, [nodesInitialized, dagData, fitView]);

  const onNodeClick = useCallback((_: React.MouseEvent, node: any) => {
    setSelectedNode(node.data);
    setIsPanelOpen(true);
  }, []);

  const healthSummary = useMemo(() => {
    const total = filteredRegistrations.length;
    const healthy = filteredRegistrations.filter((r: any) => r.status === 'HEALTHY').length;
    const unhealthy = filteredRegistrations.filter((r: any) => r.status === 'UNHEALTHY').length;
    const draining = filteredRegistrations.filter((r: any) => r.status === 'DRAINING').length;
    return { total, healthy, unhealthy, draining };
  }, [filteredRegistrations]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <SpinningLoader />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: '500px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 0',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <ContentSwitcher defaultValue={ephemeralFilter} onValueChange={setEphemeralFilter}>
            <ContentSwitcher.Option value='all' label='All Seeders' />
            <ContentSwitcher.Option value='ephemeral' label='Ephemeral Only' />
            <ContentSwitcher.Option value='permanent' label='Permanent Only' />
          </ContentSwitcher>

          <div style={{ height: '20px', width: '1px', backgroundColor: '#A1A8B0' }} />

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Chip variant='success' size='default'>
              {healthSummary.healthy} healthy
            </Chip>
            {healthSummary.unhealthy > 0 && (
              <Chip variant='critical' size='default'>
                {healthSummary.unhealthy} unhealthy
              </Chip>
            )}
            {healthSummary.draining > 0 && (
              <Chip variant='neutral' size='default'>
                {healthSummary.draining} draining
              </Chip>
            )}
          </div>

          <div style={{ height: '20px', width: '1px', backgroundColor: '#A1A8B0' }} />

          <span style={{ fontSize: '12px', color: '#6F7780' }}>
            {dagData.nodes.length} dataset{dagData.nodes.length !== 1 ? 's' : ''} &middot; {healthSummary.total} seeder
            {healthSummary.total !== 1 ? 's' : ''}
          </span>
        </div>

        <Button variant='secondary' size='small' iconName='refresh' onClick={() => refetch()}>
          Refresh
        </Button>
      </div>

      <div
        style={{
          flex: 1,
          borderRadius: '8px',
          border: `1px solid #A1A8B0`,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.15, maxZoom: 1.25 }}
          minZoom={0.1}
          proOptions={{ hideAttribution: true }}
        >
          <Background color='#e8e8e8' gap={24} size={1} />
          <Controls position='bottom-left' showInteractive={false} />
          <MiniMap
            position='bottom-right'
            pannable
            zoomable
            nodeColor={(node: any) => {
              const status = node.data?.aggregateStatus;
              if (status === 'HEALTHY') return '#4caf50';
              if (status === 'DEGRADED') return '#ff9800';
              if (status === 'UNHEALTHY') return '#f44336';
              return '#b0bec5';
            }}
          />
        </ReactFlow>

        {!isPanelOpen && dagData.nodes.length > 0 && (
          <div
            style={{
              position: 'absolute',
              bottom: '12px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(0,0,0,0.6)',
              color: 'white',
              padding: '5px 14px',
              borderRadius: '16px',
              fontSize: '11px',
              zIndex: 5,
              pointerEvents: 'none',
            }}
          >
            Click a dataset node to inspect its seeders
          </div>
        )}
      </div>

      <NodeDetailPanel nodeData={selectedNode} isOpen={isPanelOpen} onClose={() => setIsPanelOpen(false)} />
    </div>
  );
};

export const DagView: React.FC = () => {
  const {
    data: registrationsData,
    isLoading,
    refetch,
  } = useListRegistrationsQuery({
    pageSize: 1000,
    pageToken: '',
    filter: '',
  });

  return (
    <ReactFlowProvider>
      <DagFlowInner registrationsData={registrationsData} isLoading={isLoading} refetch={refetch} />
    </ReactFlowProvider>
  );
};
